import { useEffect, useState } from "react";
import { Box, Checkbox, Grid, FormControlLabel } from "@mui/material";
import { FilterButton, GlobalSecondaryButton } from "../../../styles";
import { applyFiltersToArray, exportToCsv } from "../../../../global";
import { GridRender, formatRegionInfo } from "./GridRender";
import { useUser } from "../../../../context/UserContext";
import { useAuth } from "../../../../context/AuthContext";
import { Roles } from "../../../../constants/roles";
import FilterMenu from "../../../../components/FilterMenu";
import Requests from "../../../../api/SDSRequest/SDSRequest";
import { requestStatusOptions } from "../constants";

const GridColumns = [
  { id: "", label: "", notSortable: true },
  { id: "id", label: "Request ID" },
  { id: "requestedByEmail", label: "Requested For" },
  { id: "sdsRequestType.description", label: "Request Type" },
  { id: "priority", label: "Priority" },
  { id: "region", label: "Region" },
  { id: "createdDate", label: "Date Created" },
  { id: "requestedCompletionDate", label: "Required Date" },
  { id: "requestStatusName", label: "Status" },
  { id: "project.name", label: "Project" },
  { id: "description", label: "Description" },
];

const ProcessSDS = ({ id }) => {
  const [showCompletedRequests, setShowCompletedRequests] = useState(false);
  const [showCancelledRequests, setShowCancelledRequests] = useState(false);
  const [showMyAssignedRequests, setShowMyAssignedRequests] = useState(false);
  const [result, setResult] = useState();
  const [originalResult, setOriginalResult] = useState();
  const [loading, setLoading] = useState(true);
  const [filteringOpen, setFilteringOpen] = useState(false);
  const [filters, setFilters] = useState([
    { name: null, displayName: null, operator: null, enumValues: [], value: "" },
  ]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [reloadData] = useState(false);
  const { username } = useUser();
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.SdsRequestWorker, Roles.Developer]);

  const getRequest = async () => {
    setLoading(true);
    const data = await Requests.getAll();
    if (data) {
      setResult(
        data.filter(
          (s) =>
            s.requestStatusName !== requestStatusOptions.draft &&
            s.requestStatusName !== requestStatusOptions.completed &&
            s.requestStatusName !== requestStatusOptions.cancelled,
        ),
      );
      setOriginalResult(data.filter((s) => s.requestStatusName !== requestStatusOptions.draft));
    }
    setLoading(false);
  };

  useEffect(() => {
    getRequest();
  }, [reloadData]);

  useEffect(() => {
    if (result) {
      let updatedResult = [...result];
      if (showMyAssignedRequests) {
        updatedResult = result.filter((s) => s.assignedToEmail === username);
      }
      // `region` is not an API field; it is computed so the generic filter can match on it.
      const withRegion = updatedResult.map((row) => ({ ...row, region: formatRegionInfo(row) }));
      const filteredArray = applyFiltersToArray(filters, withRegion).map(
        ({ region, ...row }) => row,
      );
      setFilteredRequests(filteredArray);
    }
  }, [username, filters, result, showMyAssignedRequests]);

  const filterClick = (event) => {
    setFilteringOpen(true);
  };

  const clearFiltersClick = (event) => {
    setFilters([{ name: null, displayName: null, operator: null, enumValues: [], value: null }]);
  };

  function closeFiltering() {
    setFilteringOpen(false);
  }

  function applyFilters() {
    setFilteringOpen(false);
  }

  function handleExportToCsv() {
    const data = filteredRequests.map((row) => {
      const transformedRow = {};
      GridColumns.slice(1).forEach((col) => {
        if (col.id === "region") {
          transformedRow[col.label] = formatRegionInfo(row);
          return;
        }

        const keys = col.id.split(".");
        let value = row;
        keys.forEach((key) => {
          value = value ? value[key] : "";
        });
        transformedRow[col.label] = value;
      });
      return transformedRow;
    });

    exportToCsv(data, "SDS_Requests.csv");
  }

  const handleCheckBoxFilter = (checked, status) => {
    let updatedResult = [...originalResult];

    let completed = showCompletedRequests;
    let cancelled = showCancelledRequests;

    if (status === requestStatusOptions.completed) {
      setShowCompletedRequests(checked);
      completed = checked;
    } else if (status === requestStatusOptions.cancelled) {
      setShowCancelledRequests(checked);
      cancelled = checked;
    }

    if (!completed && !cancelled) {
      updatedResult = originalResult.filter(
        (s) =>
          s.requestStatusName !== requestStatusOptions.completed &&
          s.requestStatusName !== requestStatusOptions.cancelled,
      );
    } else if (completed && !cancelled) {
      updatedResult = originalResult.filter(
        (s) => s.requestStatusName !== requestStatusOptions.cancelled,
      );
    } else if (!completed && cancelled) {
      updatedResult = originalResult.filter(
        (s) => s.requestStatusName !== requestStatusOptions.completed,
      );
    } else if (completed && cancelled) {
      updatedResult = [...originalResult];
    }

    if (status === "AssignedToMe") setShowMyAssignedRequests(checked);

    setResult(updatedResult);
  };

  const filterOptions = [
    { name: "id", displayName: "Request ID", type: "string", enumValues: [] },
    { name: "requestedByEmail", displayName: "Requested For", type: "string", enumValues: [] },
    {
      name: "sdsRequestType.description",
      displayName: "Request Type",
      type: "string",
      enumValues: [],
    },
    { name: "priority", displayName: "Priority", type: "string", enumValues: [] },
    { name: "region", displayName: "Region", type: "string", enumValues: [] },
    { name: "createdDate", displayName: "Date Created", type: "date", enumValues: [] },
    { name: "requestedCompletionDate", displayName: "Required Date", type: "date", enumValues: [] },
    { name: "requestStatusName", displayName: "Status", type: "string", enumValues: [] },
    { name: "project.name", displayName: "Project", type: "string", enumValues: [] },
    { name: "description", displayName: "Description", type: "string", enumValues: [] },
  ];

  return (
    <>
      <Grid container justifyContent="right" style={{ marginTop: "10px" }}>
        <FormControlLabel
          sx={{ justifyContent: "right" }}
          control={<Checkbox />}
          onChange={(e) => {
            handleCheckBoxFilter(e.target.checked, requestStatusOptions.completed);
          }}
          checked={showCompletedRequests}
          label="Show Completed Requests"
        />
        <FormControlLabel
          sx={{ justifyContent: "right" }}
          control={<Checkbox />}
          onChange={(e) => {
            handleCheckBoxFilter(e.target.checked, requestStatusOptions.cancelled);
          }}
          checked={showCancelledRequests}
          label="Show Cancelled Requests"
        />
        <FormControlLabel
          sx={{ justifyContent: "right" }}
          control={<Checkbox />}
          onChange={(e) => {
            handleCheckBoxFilter(e.target.checked, "AssignedToMe");
          }}
          checked={showMyAssignedRequests}
          label="Show Only Requests Assigned To Me"
        />
        <Box display="flex" alignItems={"center"} marginLeft="auto" marginTop="-.8rem">
          <FilterButton
            variant="contained"
            filtered={!(filters[0].name === null)}
            onClick={() => filterClick()}>
            Filters
          </FilterButton>

          {!(filters[0].name === null) && (
            <GlobalSecondaryButton
              style={{ marginRight: "1rem", marginLeft: "1rem" }}
              variant="contained"
              onClick={() => clearFiltersClick()}>
              Clear Filters
            </GlobalSecondaryButton>
          )}

          {filteredRequests?.length && (
            <GlobalSecondaryButton variant="contained" onClick={() => handleExportToCsv()}>
              Export CSV
            </GlobalSecondaryButton>
          )}
        </Box>
      </Grid>
      <br />
      <GridRender
        gridColumns={GridColumns}
        canEdit={canEdit}
        reloadList={getRequest}
        requests={filteredRequests}
        showCompletedRequests={showCompletedRequests}
        showCancelledRequests={showCancelledRequests}
        loading={loading}
        id={id}
      />
      <FilterMenu
        moduleName="sds-process"
        open={filteringOpen}
        setOpen={setFilteringOpen}
        applyBtnAction={applyFilters}
        cancelButtonAction={closeFiltering}
        filteringInfo={filterOptions}
        appliedFilters={filters}
        setAppliedFilters={setFilters}
      />
    </>
  );
};

export default ProcessSDS;
